import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Country } from '../types';
import { internationalService } from '../services/payment/internationalService';

interface InternationalContextValue {
  countries: Country[];
  selected: Country | null;
  select: (code: string) => void;
  loading: boolean;
}

const InternationalContext = createContext<InternationalContextValue | undefined>(undefined);

export function InternationalProvider({ children }: { children: ReactNode }) {
  const [countries, setCountries] = useState<Country[]>([]);
  const [selected, setSelected] = useState<Country | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    internationalService.listCountries().then((res) => {
      if (res.ok) setCountries(res.data);
      setLoading(false);
    });
  }, []);

  const select = useCallback(
    (code: string) => setSelected(countries.find((c) => c.code === code) ?? null),
    [countries],
  );

  const value = useMemo<InternationalContextValue>(
    () => ({ countries, selected, select, loading }),
    [countries, selected, select, loading],
  );

  return <InternationalContext.Provider value={value}>{children}</InternationalContext.Provider>;
}

export function useInternationalContext() {
  const ctx = useContext(InternationalContext);
  if (!ctx) throw new Error('useInternationalContext must be used within an InternationalProvider');
  return ctx;
}
